import type { EscoHrDisplayStatus } from './types';

/**
 * Approved escoHR copy and labels, shared by the Time & Attendance panel and the Time Off, Holidays and Expenses sections so they can never
 * drift apart (docs/escohr-time-attendance-contract.md section 18). Nothing here may say or imply that time, leave, holidays or reimbursements
 * are sent to payroll, that payroll is ready, or that sync is automatic, unless the server reports Operational.
 */
export const LOCKED_TITLE = 'Time & Attendance is not available yet';
export const LOCKED_MESSAGE =
  'escoHR Mobile Time & Attendance integration is being prepared. Once it is operational and activated for your company, employees will be able to record time in escoHR and approved timecards can be reviewed for payroll in EPN.';
export const TESTING_ONLY_MESSAGE = 'Connection configured for testing only. Mobile punches and timecards are not currently used in payroll.';
export const OPERATIONAL_MESSAGE = 'Employees can record time in escoHR. Only reviewed and approved timecards are available for payroll in EPN.';

export const displayStatusLabels: Record<EscoHrDisplayStatus, string> = {
  NotConfigured: 'Not Configured',
  ConfigurationRequired: 'Configuration Required',
  ConfiguredTestingOnly: 'Configured — Testing Only',
  ConnectedNotOperational: 'Connected — Not Operational',
  Operational: 'Operational',
  SyncError: 'Sync Error',
  Suspended: 'Suspended',
};

/** Reasons a plan or subscription change could resolve; only these get a View Plans link, because only then does the link lead anywhere useful. */
export const PLAN_REASONS = new Set(['PlanUpgradeRequired', 'TrialNotStarted', 'TrialExpired', 'SubscriptionCanceled', 'PaymentFailed', 'NoSubscription']);

export function lockedReason(reason: string | null): string {
  switch (reason) {
    case 'FeatureNotAvailable':
      return 'This capability is not available in this environment yet.';
    case 'PlanUpgradeRequired':
      return 'It is not included in your current plan.';
    case 'TrialNotStarted':
    case 'TrialExpired':
    case 'SubscriptionCanceled':
    case 'PaymentFailed':
    case 'NoSubscription':
      return 'Your subscription does not currently include plan features.';
    default:
      return 'It is not available to your company right now.';
  }
}

export function formatWhen(value: string | null | undefined): string {
  return value ? new Date(value).toLocaleString() : '—';
}

/** The one line that explains what the current status does and does not mean for payroll. Null where the status needs no extra words. */
export function statusNote(display: EscoHrDisplayStatus): string | null {
  switch (display) {
    case 'ConfigurationRequired':
      return 'Setup is not finished. Generate a signing secret and give it, with the connection reference, to escoHR.';
    case 'ConfiguredTestingOnly':
    case 'ConnectedNotOperational':
      return TESTING_ONLY_MESSAGE;
    case 'SyncError':
      return 'The latest event from escoHR could not be accepted. See the sync history. Mobile punches and timecards are not currently used in payroll.';
    case 'Operational':
      return OPERATIONAL_MESSAGE;
    default:
      return null;
  }
}
